import { FC, ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Editor } from 'react-draft-wysiwyg';
import { Form, Field } from 'react-final-form';
import moment from 'moment';
import { format, parseISO } from 'date-fns';
import { convertToRaw } from 'draft-js';

import { Box, Button, Loader, RFFTextInput } from '@qwealth/qcore';
import { dateFormat, QNote, QTask, selectUser } from '@qwealth/qdata';
import { commonSubscription, DocumentUploader, InputForm } from '@qwealth/kyc';

import AttachmentList from 'components/common/AttachmentList';
import { UploaderWithoutPreview } from 'components/common/UploaderWithoutPreview';
import KYCFlags from 'components/common/KYCFlags';
import { buildAllMembers, getCheckedMemberQids } from 'data/helpers/meaningfulInteraction';
import { loadHouseholdNotes, saveQNote } from 'data/actions/qnotes';
import { deleteQNoteAttachments } from 'data/data-layer/qnote';
import { deleteEmbeddedAttachments } from 'data/data-layer/attachments';
import qnoteValidator from 'data/validations/qnote';
import { useInitializedEditorState } from 'hooks/useInitializedEditorState';
import { useQvault2Flag } from 'hooks/useFeatureFlag';
import { EditorWrapper } from 'components/common/EditorWrapper';
import { MeaningfulContent } from 'components/common/MeaningfulContent';
import TransitionBox from '../TransitionBox';
import QNoteAuthorData from './QNoteAuthorData';
import WealthboxWarningMessage from './WealthboxWarningMessage';
import QNoteVersionSelector from './QNoteVersionSelector';
import { useAppDispatch } from 'data/store';
import { selectClientMembers, selectHouseholdLegalEntities } from '../../../../../data/selectors/householdSelectors';
import { getQnoteAttachmentPath } from 'data/helpers/qnoteFilterHelper';
import { QNoteFormValues } from 'data/models/qnoteForm';

interface QNoteFormProps {
  note?: QNote | null;
  noteVersions?: QNote[];
  hasWealthboxConflict?: boolean;
  householdQID: string;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  onClose: () => void;
  setIsTaskModalOpen?: (isOpen: boolean) => void;
  task?: Partial<QTask> | null;
  isQnoteModalFromTask?: boolean;
}

export const QNoteForm: FC<QNoteFormProps> = ({
  note,
  noteVersions,
  hasWealthboxConflict,
  householdQID,
  isLoading,
  setIsLoading,
  onClose,
  setIsTaskModalOpen,
  task,
  isQnoteModalFromTask,
}): ReactElement => {
  const user = useSelector(selectUser);
  const clientMembers = useSelector(selectClientMembers);
  const legalEntities = useSelector(selectHouseholdLegalEntities);
  const [qvault2Enabled, qvault2Loaded] = useQvault2Flag();
  const [attachmentsPath, setAttachmentsPath] = useState<string>('');
  const [queuedDeletionIds, setQueuedDeletionIds] = useState<string[]>([]);

  const [editorState, setEditorState] = useInitializedEditorState(note);
  const [displayedNote, setDisplayedNote] = useState<QNote>();
  useEffect(() => {
    changeDisplayedNote(note);
  }, []);
  useEffect(() => {
    if (qvault2Loaded && !qvault2Enabled && note?.uuid) {
      setAttachmentsPath(getQnoteAttachmentPath(householdQID, note.uuid));
    } else {
      setAttachmentsPath('');
    }
  }, [note, qvault2Enabled, qvault2Loaded, householdQID]);

  const changeDisplayedNote = (noteToDisplay: QNote | undefined | null) => {
    setDisplayedNote({
      ...noteToDisplay,
      owner: noteToDisplay?.owner ?? [],
      title: noteToDisplay?.title ?? '',
      author: noteToDisplay?.author ?? `${user.firstName} ${user.lastName}`,
      date: noteToDisplay?.date ?? moment().format(dateFormat),
      content: noteToDisplay?.content ?? '',
      isSignificantChange: noteToDisplay?.isSignificantChange || 0,
      isMeaningfulInteraction: noteToDisplay?.isMeaningfulInteraction || 0,
      isPortfolioReview: noteToDisplay?.isPortfolioReview || 0,
    } as QNote);
  };

  const dispatch = useAppDispatch();

  const submitNote = (values: QNoteFormValues) => {
    (async () => {
      const { relatedMembersChecked, selectAllRelatedMembers, attachmentsToDelete, ...rest } = values;
      const relatedMembers = getCheckedMemberQids(allMembers, relatedMembersChecked);

      setIsLoading(true);
      try {
        await dispatch(
          saveQNote({
            ...rest,
            relatedMembers,
            noteContent: JSON.stringify(convertToRaw(editorState.getCurrentContent())),
            householdQID,
          }, qvault2Enabled),
        );
      } catch {
        setIsLoading(false);
        return;
      }
      if (attachmentsToDelete?.length) {
        await deleteQNoteAttachments(attachmentsToDelete);
      }
      if (queuedDeletionIds.length) {
        await deleteEmbeddedAttachments(queuedDeletionIds, householdQID);
        setQueuedDeletionIds([]);
      }
      await dispatch(loadHouseholdNotes(householdQID));
      setIsLoading(false);
      onClose();
    })();
  };

  const allMembers = buildAllMembers(clientMembers, legalEntities);
  const existingAttachments = displayedNote?.attachments ?? [];

  return (
    <Box width="100%" display="flex" alignItems="center" justifyContent="center">
      {isLoading ? (
        <Loader />
      ) : (
        <TransitionBox index={1} isVisible={true} width="90%">
          <Box marginTop="large">
            <Form<QNoteFormValues>
              initialValues={displayedNote}
              onSubmit={submitNote}
              validate={values => qnoteValidator(values, editorState)}
            >
              {({ handleSubmit, submitting, values }) => {
                return (
                  <Box display="flex" flexDirection="column">
                    <Box display="flex" flexDirection="row">
                      <Box marginRight="largest" display="flex" flexDirection="column" gap="large">
                        <Box display="flex" flexDirection="row" gap="largest">
                          <InputForm width="275px">
                            <RFFTextInput name="title" label="Title*" subscription={commonSubscription} />
                          </InputForm>
                        </Box>
                        {displayedNote && (
                          <Box display="flex" flexDirection="column">
                            <Box fontWeight="bold">KYC Flags</Box>
                            <KYCFlags kycFlags={displayedNote} mt="default" mb="default" />
                          </Box>
                        )}
                      </Box>

                      <Box display="flex" flexDirection="column" width="100%">
                        {hasWealthboxConflict && <QNoteVersionSelector qnotes={[note as QNote, note as QNote]} onChange={changeDisplayedNote} />}
                        {displayedNote && (
                          <QNoteAuthorData
                            createdDate={displayedNote.date}
                            createdBy={displayedNote.author}
                            //@ts-ignore ToDo: Check why qnote model in qdata did not update properly
                            updatedDate={displayedNote.lastUpdated ? format(parseISO(displayedNote.lastUpdated), 'yyyy-MM-dd') : ''}
                            updatedBy={displayedNote.lastUpdatedBy}
                            task={task}
                            noteId={displayedNote.uuid}
                            setIsTaskModalOpen={setIsTaskModalOpen}
                            isQnoteModalFromTask={isQnoteModalFromTask}
                          />
                        )}
                        {hasWealthboxConflict && <WealthboxWarningMessage />}
                      </Box>
                    </Box>

                    {/* @ts-ignore TODO: remove after version bump */}
                    <MeaningfulContent allMembers={allMembers} disabledQids={note?.relatedMembers || []} />

                    <Box fontWeight="bold" marginBottom="default" marginTop="large">
                      Content*
                    </Box>
                    <Box>
                      <Field name="content" subscription={commonSubscription}>
                        {({ input, meta }) => (
                          <EditorWrapper hasError={!!meta.error && meta.touched}>
                            <Editor
                              editorState={editorState}
                              handlePastedText={() => false}
                              onEditorStateChange={editorState => {
                                setEditorState(editorState);
                                input.onChange((Math.random() + 1).toString(36).substring(7));
                              }}
                              editorClassName="wysiwygEditor"
                              onBlur={() => {
                                input.onBlur();
                              }}
                              spellCheck
                            />
                            {meta.error && meta.touched && <Box color="error">{meta.error}</Box>}
                          </EditorWrapper>
                        )}
                      </Field>
                    </Box>

                    <Box marginTop="large">
                      {qvault2Loaded && (qvault2Enabled ? (
                        <UploaderWithoutPreview>
                          <DocumentUploader
                            title="QNotes Attachments"
                            label="QNotes Attachments"
                            editing
                            isMulti
                            directPath=""
                          />
                        </UploaderWithoutPreview>
                      ) : (
                        <DocumentUploader
                          title="QNotes Attachments"
                          label="QNotes Attachments"
                          editing
                          isMulti
                          directPath={attachmentsPath}
                          isFileDeletable={!values.isMeaningfulInteraction && !values.isSignificantChange && !values.isPortfolioReview}
                        />
                      ))}
                      {qvault2Loaded && qvault2Enabled && (
                        <AttachmentList
                          attachments={existingAttachments}
                          householdQID={householdQID}
                          isFileDeletable={!values.isMeaningfulInteraction && !values.isSignificantChange && !values.isPortfolioReview}
                          label="Uploaded Attachments"
                          onQueueDelete={id => setQueuedDeletionIds(prev => [...prev, id])}
                        />
                      )}
                    </Box>
                    <Field name="pending" subscription={commonSubscription}>
                      {() => null}
                    </Field>

                    <Box display="flex" justifyContent="end" gap="default" paddingTop="large" marginBottom="large">
                      <Button onClick={handleSubmit} disabled={submitting}>
                        Save
                      </Button>
                      <Button variant="outline" onClick={onClose}>
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                );
              }}
            </Form>
          </Box>
        </TransitionBox>
      )}
    </Box>
  );
};
